import { safeReturnPath } from '../hooks/useListState'

/**
 * RETOUR APRÈS RECONNEXION (#262).
 *
 * Après une session expirée (iPad réveillé, jeton mort : `services/api.ts`
 * renvoie sur `/login` par un rechargement COMPLET), une déconnexion forcée
 * par l'admin, ou un lien ouvert sans session, l'agent était envoyé sur le
 * tableau de bord au lieu de l'écran où il travaillait.
 *
 * Deux sources, parce que le rechargement complet efface `location.state` :
 *   · `location.state.from` — posé par la redirection de la route non
 *     authentifiée (`RedirectToLogin`, `App.tsx`) : lien profond ouvert sans
 *     session, déconnexion sans rechargement ;
 *   (la cible est choisie par la route `/login` de l'arbre AUTHENTIFIÉ,
 *   `routes/publicOnlyRedirects.tsx` — une seule source, pas la page)
 *   · `sessionStorage` — la dernière page AUTHENTIFIÉE de l'onglet, avec
 *     l'identifiant de son utilisateur (`ReturnToTracker`) : elle survit au
 *     `window.location.href = '/login'` d'`api.ts` (zone gelée, non modifiée).
 *
 * Un autre utilisateur qui se connecte sur le même iPad (changement d'agent)
 * ne reprend JAMAIS la page du précédent : la mémoire porte l'identifiant de
 * son propriétaire, et un désaccord renvoie à l'atterrissage par rôle (`/`).
 */
export const LOGIN_RETURN_KEY = 'auth:returnTo'

/** Atterrissage par rôle (`RoleLanding`, décision UX D-1). */
export const DEFAULT_LOGIN_TARGET = '/'

/** Chemins qui ne sont pas un « écran de travail » à retrouver. */
const NOT_A_DESTINATION = new Set(['/', '/login', '/register'])

interface StoredReturn {
  path: string
  userId: string
}

export function rememberAuthenticatedRoute(path: string, userId: string): void {
  if (!safeReturnPath(path) || NOT_A_DESTINATION.has(path.split('?')[0])) return
  try {
    window.sessionStorage.setItem(LOGIN_RETURN_KEY, JSON.stringify({ path, userId } satisfies StoredReturn))
  } catch {
    /* stockage indisponible : le retour se fera vers l'atterrissage par rôle. */
  }
}

function readStoredReturn(): StoredReturn | null {
  try {
    const raw = window.sessionStorage.getItem(LOGIN_RETURN_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<StoredReturn>
    const path = safeReturnPath(parsed.path)
    return path && typeof parsed.userId === 'string' ? { path, userId: parsed.userId } : null
  } catch {
    return null
  }
}

/** Efface la mémoire d'onglet : elle ne sert qu'à UNE reconnexion. */
export function consumeLoginReturn(): void {
  try {
    window.sessionStorage.removeItem(LOGIN_RETURN_KEY)
  } catch {
    /* stockage indisponible : rien à effacer. */
  }
}

/**
 * Où aller après une connexion réussie de `userId`.
 *   1. une mémoire d'onglet appartenant à un AUTRE utilisateur ⇒ l'accueil ;
 *   2. sinon `state.from` (sûr, interne) ;
 *   3. sinon la mémoire d'onglet du même utilisateur ;
 *   4. sinon l'accueil.
 *
 * L-NAV-8 — « l'accueil » = `home`, l'accueil du rôle (`routes/roleHome.ts`,
 * `loginHomeFor` : `/accueil` pour l'admin) ; défaut `/` (agent inchangé).
 */
export function resolveLoginTarget(
  stateFrom: unknown,
  userId: string | undefined,
  home: string = DEFAULT_LOGIN_TARGET,
): string {
  const stored = readStoredReturn()
  if (stored && stored.userId !== userId) return home
  const from = safeReturnPath(stateFrom)
  if (from && !NOT_A_DESTINATION.has(from.split('?')[0])) return from
  return stored?.path ?? home
}
